import React, { FormEvent, useEffect, useState } from 'react';
import api from '../../services/api';

type WineryStatus = 'active' | 'inactive' | 'trial';

interface Winery {
  id: number;
  name: string;
  slug: string;
  status: WineryStatus;
  contactEmail?: string | null;
  createdAt?: string;
}

interface CreatedAdminResult {
  email: string;
  defaultPassword: string;
  wineryName: string;
}

const statusStyles: Record<WineryStatus, string> = {
  active: 'bg-accent-50 text-accent-700 ring-accent-100',
  trial: 'bg-secondary-50 text-secondary-700 ring-secondary-100',
  inactive: 'bg-neutral-100 text-neutral-600 ring-neutral-200',
};

const Wineries: React.FC = () => {
  const [wineries, setWineries] = useState<Winery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [status, setStatus] = useState<WineryStatus>('active');

  const [adminWinery, setAdminWinery] = useState<Winery | null>(null);
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminSubmitting, setAdminSubmitting] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [createdAdmin, setCreatedAdmin] = useState<CreatedAdminResult | null>(null);

  const loadWineries = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<Winery[]>('/api/platform/wineries');
      setWineries(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Unable to load wineries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWineries();
  }, []);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      await api.post('/api/platform/wineries', {
        name: name.trim(),
        contact_email: contactEmail.trim() || undefined,
        status,
      });
      setName('');
      setContactEmail('');
      setStatus('active');
      await loadWineries();
    } catch {
      setFormError('Unable to create winery.');
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateAdmin = (winery: Winery) => {
    setAdminWinery(winery);
    setAdminFirstName('');
    setAdminLastName('');
    setAdminEmail(winery.contactEmail?.trim() || '');
    setAdminError(null);
    setCreatedAdmin(null);
  };

  const closeAdminModal = () => {
    setAdminWinery(null);
    setAdminError(null);
    setCreatedAdmin(null);
    setAdminSubmitting(false);
  };

  const handleCreateAdmin = async (e: FormEvent) => {
    e.preventDefault();
    if (!adminWinery) return;

    if (!adminFirstName.trim() || !adminLastName.trim() || !adminEmail.trim()) {
      setAdminError('First name, last name, and email are required.');
      return;
    }

    try {
      setAdminSubmitting(true);
      setAdminError(null);
      const res = await api.post<{
        email: string;
        defaultPassword?: string;
        default_password?: string;
      }>(`/api/platform/wineries/${adminWinery.id}/admin-user`, {
        first_name: adminFirstName.trim(),
        last_name: adminLastName.trim(),
        email: adminEmail.trim(),
      });

      const defaultPassword =
        res.data.defaultPassword || res.data.default_password || '';

      setCreatedAdmin({
        email: res.data.email,
        defaultPassword,
        wineryName: adminWinery.name,
      });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Unable to create admin user.';
      setAdminError(typeof message === 'string' ? message : 'Unable to create admin user.');
    } finally {
      setAdminSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Wineries</h1>
        <p className="mt-1 text-sm text-neutral-500">Manage winery tenants on the platform.</p>
      </div>

      <div className="mb-8 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-neutral-800 mb-4">Create winery</h2>
        {formError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}
          </div>
        )}
        <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="winery-name" className="block text-xs font-medium text-neutral-600 mb-1">
              Name
            </label>
            <input
              id="winery-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="Winery name"
            />
          </div>
          <div>
            <label htmlFor="winery-email" className="block text-xs font-medium text-neutral-600 mb-1">
              Contact email
            </label>
            <input
              id="winery-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="owner@winery.com"
            />
          </div>
          <div>
            <label htmlFor="winery-status" className="block text-xs font-medium text-neutral-600 mb-1">
              Status
            </label>
            <select
              id="winery-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as WineryStatus)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60 transition-colors"
            >
              {submitting ? 'Creating…' : 'Create winery'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-neutral-200 border-t-primary-500" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Slug
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {wineries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-neutral-500">
                      No wineries yet.
                    </td>
                  </tr>
                ) : (
                  wineries.map((winery) => (
                    <tr key={winery.id} className="hover:bg-neutral-50/80">
                      <td className="px-4 py-3 font-medium text-neutral-900">{winery.name}</td>
                      <td className="px-4 py-3 text-neutral-600">{winery.slug}</td>
                      <td className="px-4 py-3 text-neutral-600">{winery.contactEmail || '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ${statusStyles[winery.status]}`}
                        >
                          {winery.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openCreateAdmin(winery)}
                          className="rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
                        >
                          Create admin
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adminWinery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/40"
            onClick={closeAdminModal}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-admin-title"
            className="relative w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-5 shadow-xl"
          >
            <h2 id="create-admin-title" className="text-lg font-semibold text-neutral-900">
              {createdAdmin ? 'Admin created' : 'Create admin'}
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              {createdAdmin
                ? `Share these credentials for ${createdAdmin.wineryName}.`
                : `Provision a winery admin for ${adminWinery.name}.`}
            </p>

            {createdAdmin ? (
              <div className="mt-4 space-y-3">
                <div className="rounded-lg border border-accent-100 bg-accent-50 px-3 py-3 text-sm text-neutral-800">
                  <div className="mb-2">
                    <span className="block text-xs font-medium text-neutral-500">Email</span>
                    <span className="select-all font-medium">{createdAdmin.email}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-neutral-500">
                      Default password
                    </span>
                    <span className="select-all font-mono font-medium">
                      {createdAdmin.defaultPassword}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500">
                  The admin can change this password after signing in.
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeAdminModal}
                    className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreateAdmin} className="mt-4 space-y-3">
                {adminError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {adminError}
                  </div>
                )}
                <div>
                  <label
                    htmlFor="admin-first-name"
                    className="block text-xs font-medium text-neutral-600 mb-1"
                  >
                    First name
                  </label>
                  <input
                    id="admin-first-name"
                    type="text"
                    value={adminFirstName}
                    onChange={(e) => setAdminFirstName(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="admin-last-name"
                    className="block text-xs font-medium text-neutral-600 mb-1"
                  >
                    Last name
                  </label>
                  <input
                    id="admin-last-name"
                    type="text"
                    value={adminLastName}
                    onChange={(e) => setAdminLastName(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="admin-email"
                    className="block text-xs font-medium text-neutral-600 mb-1"
                  >
                    Email
                  </label>
                  <input
                    id="admin-email"
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeAdminModal}
                    className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={adminSubmitting}
                    className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
                  >
                    {adminSubmitting ? 'Creating…' : 'Create admin'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Wineries;
